import { MockServiceStore } from "./MockServiceStore";
import { mockStructureCastComponents } from "./structurecast-components.mock";

/**
 * QC Check Item — a single line on an inspection checklist
 */
export interface QcCheckItem {
  itemId: string;
  label: string;
  checked: boolean;
  notes: string;
  /** For dimensional checks (post-pour section 2) */
  specified?: string;
  measured?: string;
  withinTolerance?: string;
}

/**
 * QC Section — a group of check items
 */
export interface QcSection {
  sectionNumber: number;
  sectionName: string;
  items: QcCheckItem[];
}

/**
 * StructureCast - QC Inspection (Pre-Pour or Post-Pour checklist per component)
 */
export interface StructureCastQcInspection {
  id: string;
  componentId: string;
  inspectionType: "pre_pour" | "post_pour";
  inspector: string;
  inspectionDate: string;
  productType: "storm_box" | "manhole_48" | "pullbox" | "cable_vault" | "manhole_rect" | "other";
  size: string;
  projectJobNumber: string;
  status: "not_started" | "in_progress" | "complete" | "failed";
  sections: QcSection[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Pre-Pour checklist template (ASTM C478 standards)
 */
const createPrePourSections = (): QcSection[] => [
  {
    sectionNumber: 1,
    sectionName: "Form & Insert Preparation",
    items: [
      { itemId: "pp-1-1", label: "Form cleaned and free of debris", checked: false, notes: "" },
      { itemId: "pp-1-2", label: "Release agent applied per spec", checked: false, notes: "" },
      { itemId: "pp-1-3", label: "Form properly secured and aligned", checked: false, notes: "" },
      { itemId: "pp-1-4", label: "Knockout forms installed per drawings", checked: false, notes: "" },
      { itemId: "pp-1-5", label: "Pull irons (1/2\" inserts) positioned at 12\" spacing", checked: false, notes: "" },
      { itemId: "pp-1-6", label: "2-ton RISS handling anchors positioned per drawings", checked: false, notes: "" },
    ],
  },
  {
    sectionNumber: 2,
    sectionName: "Reinforcement Checks",
    items: [
      { itemId: "pp-2-1", label: "ASTM A-615 Grade 60 rebar placed per drawings", checked: false, notes: "" },
      { itemId: "pp-2-2", label: "Rebar clearance and cover adequate (min 1\" clear)", checked: false, notes: "" },
      { itemId: "pp-2-3", label: "Tie wires secure and trimmed", checked: false, notes: "" },
    ],
  },
  {
    sectionNumber: 3,
    sectionName: "Additional Items",
    items: [
      { itemId: "pp-3-1", label: "Knockout positions verified per drawing", checked: false, notes: "" },
      { itemId: "pp-3-2", label: "Handling anchor locations confirmed", checked: false, notes: "" },
      { itemId: "pp-3-3", label: "Dimensions double-checked against shop drawings", checked: false, notes: "" },
    ],
  },
];

/**
 * Post-Pour checklist template (ASTM C478 standards)
 */
const createPostPourSections = (lengthIn: number, widthIn: number, heightIn: number, wallThickness: number): QcSection[] => [
  {
    sectionNumber: 1,
    sectionName: "Visual Inspection",
    items: [
      { itemId: "po-1-1", label: "Surface finish — no excessive bugholes or voids", checked: false, notes: "" },
      { itemId: "po-1-2", label: "Corners and edges intact — no chips or cracks", checked: false, notes: "" },
      { itemId: "po-1-3", label: "Handling anchors accessible and undamaged", checked: false, notes: "" },
      { itemId: "po-1-4", label: "Mark number and structure ID legible", checked: false, notes: "" },
      { itemId: "po-1-5", label: "Knockouts clean and properly formed", checked: false, notes: "" },
    ],
  },
  {
    sectionNumber: 2,
    sectionName: "Dimensional Check",
    items: [
      { itemId: "po-2-1", label: "Length/Diameter", checked: false, notes: "", specified: `${lengthIn}"`, measured: "", withinTolerance: "" },
      { itemId: "po-2-2", label: "Width", checked: false, notes: "", specified: `${widthIn}"`, measured: "", withinTolerance: "" },
      { itemId: "po-2-3", label: "Height", checked: false, notes: "", specified: `${heightIn}"`, measured: "", withinTolerance: "" },
      { itemId: "po-2-4", label: "Wall Thickness", checked: false, notes: "", specified: `${wallThickness}"`, measured: "", withinTolerance: "" },
    ],
  },
  {
    sectionNumber: 3,
    sectionName: "Embedded Items Check",
    items: [
      { itemId: "po-3-1", label: "Pull irons properly positioned at 12\" spacing", checked: false, notes: "" },
      { itemId: "po-3-2", label: "Knockouts clear and sealed", checked: false, notes: "" },
      { itemId: "po-3-3", label: "RISS handling anchors secure", checked: false, notes: "" },
    ],
  },
  {
    sectionNumber: 4,
    sectionName: "Pour Inventory",
    items: [
      { itemId: "po-4-1", label: "Pour inventory sent to office", checked: false, notes: "" },
    ],
  },
];

/**
 * Generate QC inspections for components that have been cast or cured.
 */
const generateQcInspections = (): StructureCastQcInspection[] => {
  const inspections: StructureCastQcInspection[] = [];
  let inspId = 1;

  const castOrCured = mockStructureCastComponents.filter(
    (c) => c.status === "cast" || c.status === "cured"
  );

  for (const comp of castOrCured) {
    // All Heritage Ranch WRRF structures are 48" sanitary sewer manholes
    const productType: StructureCastQcInspection["productType"] = "manhole_48";

    const size = `${comp.length}" x ${comp.width}"`;

    // Pre-Pour inspection — always complete for cast/cured components
    const prePourSections = createPrePourSections();
    for (const section of prePourSections) {
      for (const item of section.items) {
        item.checked = true;
      }
    }

    inspections.push({
      id: `sc-qc-${String(inspId).padStart(3, "0")}`,
      componentId: comp.id,
      inspectionType: "pre_pour",
      inspector: "QC Lead",
      inspectionDate: comp.actualCastDate || comp.scheduledDate,
      productType,
      size,
      projectJobNumber: "25-1585",
      status: "complete",
      sections: prePourSections,
      createdAt: comp.actualCastDate ? `${comp.actualCastDate}T08:00:00.000Z` : "2026-01-06T08:00:00.000Z",
      updatedAt: comp.actualCastDate ? `${comp.actualCastDate}T09:00:00.000Z` : "2026-01-06T09:00:00.000Z",
    });
    inspId++;

    // Post-Pour inspection
    const wallThickness = comp.componentType === "base" ? 6 : comp.componentType === "riser" ? 5 : 4;
    const compHeightIn = comp.heightFeet * 12 + comp.heightInches;
    const postPourSections = createPostPourSections(comp.length, comp.width, compHeightIn, wallThickness);

    if (comp.status === "cured") {
      for (const section of postPourSections) {
        for (const item of section.items) {
          item.checked = true;
          if (item.specified) {
            item.measured = item.specified;
            item.withinTolerance = "Yes";
          }
        }
      }

      inspections.push({
        id: `sc-qc-${String(inspId).padStart(3, "0")}`,
        componentId: comp.id,
        inspectionType: "post_pour",
        inspector: "QC Lead",
        inspectionDate: comp.actualCastDate || comp.scheduledDate,
        productType,
        size,
        projectJobNumber: "25-1585",
        status: "complete",
        sections: postPourSections,
        createdAt: comp.actualCastDate ? `${comp.actualCastDate}T14:00:00.000Z` : "2026-01-06T14:00:00.000Z",
        updatedAt: comp.actualCastDate ? `${comp.actualCastDate}T15:00:00.000Z` : "2026-01-06T15:00:00.000Z",
      });
    } else {
      for (const item of postPourSections[0].items) {
        item.checked = true;
      }

      inspections.push({
        id: `sc-qc-${String(inspId).padStart(3, "0")}`,
        componentId: comp.id,
        inspectionType: "post_pour",
        inspector: "QC Lead",
        inspectionDate: comp.actualCastDate || comp.scheduledDate,
        productType,
        size,
        projectJobNumber: "25-1585",
        status: "in_progress",
        sections: postPourSections,
        createdAt: comp.actualCastDate ? `${comp.actualCastDate}T14:00:00.000Z` : "2026-01-06T14:00:00.000Z",
        updatedAt: comp.actualCastDate ? `${comp.actualCastDate}T14:30:00.000Z` : "2026-01-06T14:30:00.000Z",
      });
    }
    inspId++;
  }

  return inspections;
};

export const mockStructureCastQcInspections: StructureCastQcInspection[] = generateQcInspections();

export const createMockStructureCastQcInspectionStore = () =>
  new MockServiceStore<StructureCastQcInspection>({
    mockData: mockStructureCastQcInspections,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-qc-inspections",
  });

export default mockStructureCastQcInspections;
